import React from 'react'
import { StorageItemFragment } from '@sketch/gql-types'

import { ConnectedDocumentItem } from '../DocumentItem'
import {
  DragAndDroppedProjectToShareIds,
  DragAndDroppedShareIds,
  useShareListItemDragDrop,
} from '../../hooks/useShareDragAndDrop'
import { DragWrapper } from './DraggableShareItem.styles'
import { Tooltip } from '@sketch/components'
import { useFlag } from '@sketch/modules-common'

type DraggableShareItemDropdown<S> = (item: S) => React.ReactNode

export interface DraggableShareItemProps<S> {
  item: S
  workspaceIdentifier: string
  userIdentifier?: string
  showProjectName?: boolean
  showCollectionName?: boolean
  renderDropdown: DraggableShareItemDropdown<S>
  presentation: 'grid' | 'list'
  onClick?: React.MouseEventHandler<HTMLElement>
  draggable?: boolean
  droppable?: boolean
  onShareDropped?: (args: DragAndDroppedShareIds) => void
  onProjectDropped?: (args: DragAndDroppedProjectToShareIds) => void
}

export const DraggableShareItem = <S extends StorageItemFragment>({
  item,
  workspaceIdentifier,
  userIdentifier,
  showProjectName,
  showCollectionName,
  renderDropdown,
  presentation,
  onClick,
  draggable: passedDownDraggable,
  droppable,
  onShareDropped,
  onProjectDropped,
}: DraggableShareItemProps<S>) => {
  const isNestedProjectsOn = useFlag('nested-projects')

  // In the list view we don't want to be able to create nested projects by
  // dragging shares onto each other so we only use `draggable` and
  // `onDragStart` and ignore the `onDropped` handlers.
  const dragAndDropEvents = useShareListItemDragDrop({
    disabled: !passedDownDraggable,
    shareId: item.identifier,
    onShareDropped,
    onProjectDropped,
  })

  const { isDraggedOver, isDraggedOverItself, isDroppableAreaEnabled } =
    dragAndDropEvents

  const documentItemsComponent = (
    <ConnectedDocumentItem
      key={item.identifier}
      workspaceId={workspaceIdentifier}
      // TODO: Fix this type
      // https://linear.app/sketch/issue/SWEB-1363
      share={item as any}
      // TODO: Fix this type
      // https://linear.app/sketch/issue/SWEB-1363
      renderDropdown={renderDropdown as any}
      userIdentifier={userIdentifier}
      showProjectName={showProjectName}
      showCollectionName={showCollectionName}
      presentation={presentation}
      onClick={onClick}
      dragAndDropEvents={dragAndDropEvents}
      droppable={droppable}
    />
  )

  const canCreateProjectByDragNDrop = Boolean(
    passedDownDraggable &&
      isDroppableAreaEnabled &&
      isDraggedOver &&
      !isDraggedOverItself &&
      (onShareDropped || onProjectDropped)
  )

  if (presentation === 'grid') {
    return (
      <DragWrapper
        data-testid="drag-wrapper"
        $isDraggedOver={canCreateProjectByDragNDrop}
        {...dragAndDropEvents}
      >
        <Tooltip
          disabled={!canCreateProjectByDragNDrop}
          content={
            isNestedProjectsOn
              ? 'Create a new project'
              : 'Create a new collection'
          }
          visible={canCreateProjectByDragNDrop}
          placement="top"
        >
          {documentItemsComponent}
        </Tooltip>
      </DragWrapper>
    )
  }

  return documentItemsComponent
}
